import {
  SchemaDefinition,
  SchemaTransformError,
  TypedNode,
  UnknownSchema
} from '@rehaje/core'

import type { EventSpecific } from './TypedNode'
import type { TypedSchema } from '@rehaje/core'

export interface SchemaEventSpecific extends RehajeSchemaMetadata {
  /**
   * 事件默认配置
   */
  default?: EventSpecific

  /**
   * 不包含的动作列表，主要给 Event Setter 消费
   */
  excludeActions?: string[]
  /**
   * 包含的动作列表，主要给 Event Setter 消费
   */
  includeActions?: string[]
  parameters?: TypedSchema[]
  type: 'Schema.Event'

  /**
   * 事件的协议
   */
  value?: EventSpecific
}

export class SchemaEventDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaEventSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default ?? { type: 'Event' }
    }
    if (typeof value === 'object' && value !== null) {
      value.type ??= 'Event'
    }
    return value
  }

  getDrillNode() {
    return this.getCurrentNode()?.getInProperty(this.getCurrentPath())
  }

  override getIn(
    schema: SchemaEventSpecific,
    path: PropertyKey[]
  ): TypedSchema {
    if (path.length === 0) return schema
    const node = this.getDrillNode()
    if (node instanceof TypedNode) {
      return node.getBelongsSchema()
    }
    return this.definition?.getPropertySchemaIn(path) ?? UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaEventSpecific,
    path: PropertyKey[]
  ): SchemaEventSpecific {
    return {
      ...schema,
      ...this.inferOnlySchema(schema, path),
      value: target
    }
  }

  override inferOnlySchema(
    schema: SchemaEventSpecific,
    path: PropertyKey[]
  ): SchemaEventSpecific {
    return {
      ...schema,
      parameters: schema.parameters?.map((parameter, index) =>
        super.inferOnlySchema(parameter, path.concat(index))
      )
    }
  }

  override inferTypeScript(schema: SchemaEventSpecific): string {
    if (typeof schema.value === 'function') return 'Function'
    return this.interface
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaEventSpecific,
    path: PropertyKey[] = []
  ) {
    if (this.test(target, schema, path)) return target
    if (target !== schema.fallback)
      this.raise(new SchemaTransformError(schema.name))
    return target
  }

  override validate(target: any, schema: SchemaEventSpecific) {
    this.validateMetadata(target, schema, (target) => {
      return typeof target === 'function' || this.isRelationSpecific(target)
    })
  }
}

declare global {
  export interface RehajeSpecifics {
    'Schema.Event': SchemaEventSpecific
  }
}
